/**
 * OmegaP0DispatchToggle — iter 138
 *
 * Settings card that toggles whether Omega should SMS/email the user when
 * the Enhancement engine flags a P0 idea. Persists to db.user_settings.
 * Lets the user fire a test dispatch immediately to verify the wiring.
 *
 * No mocks: writes to the real /api/user/state endpoint and triggers the
 * real /api/omega/enhancement-ideas/dispatch endpoint which uses the
 * existing Twilio + Resend integrations.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OmegaP0DispatchToggle() {
  const [enabled, setEnabled] = useState(null); // null = unknown, then true/false
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [testing, setTesting] = useState(false);

  // Load current state once
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user/state`).then(r => {
      if (!alive) return;
      const s = r.data || {};
      setEnabled(!!s.alertOnP0Enhancement);
      setPhone(s.notifyPhone || s.phone || "");
      setEmail(s.notifyEmail || s.email || "");
    }).catch(() => { if (alive) setEnabled(false); });
    axios.get(`${API}/omega/enhancement-ideas/dispatch/history?limit=5`).then(r => {
      if (alive) setHistory(r.data?.history || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const saveToggle = async (next) => {
    setEnabled(next);
    setSaving(true);
    try {
      await axios.put(`${API}/user/state`, {
        alertOnP0Enhancement: next,
        notifyPhone: phone || undefined,
        notifyEmail: email || undefined,
      });
      toast.success(next ? "P0 dispatch enabled — Omega will SMS/email you on P0 ideas" : "P0 dispatch disabled");
    } catch (e) {
      setEnabled(!next); // revert
      toast.error(`Couldn't save: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const saveContacts = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/state`, {
        notifyPhone: phone || undefined,
        notifyEmail: email || undefined,
      });
      toast.success("Notification contacts saved");
    } catch (e) {
      toast.error(`Couldn't save contacts: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const fireTest = async () => {
    setTesting(true);
    try {
      const r = await axios.post(`${API}/omega/enhancement-ideas/dispatch`);
      const d = r.data || {};
      if (d.sent > 0) {
        toast.success(`Dispatched to ${d.channels.join(" + ")} · ${d.sent} idea(s) sent`);
      } else if (d.reason === "no_p0") {
        toast.message("No P0 ideas in current cache to dispatch");
      } else if (d.reason === "disabled") {
        toast.warning("Dispatch is disabled — flip the toggle ON first");
      } else {
        toast.message(`Skipped ${d.skipped || 0} (already alerted in last 24h)`);
      }
      // Refresh history
      const h = await axios.get(`${API}/omega/enhancement-ideas/dispatch/history?limit=5`);
      setHistory(h.data?.history || []);
    } catch (e) {
      toast.error(`Test failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="card" data-testid="p0-dispatch-toggle" style={{ marginTop: 14, borderColor: "rgba(167,139,250,0.28)" }}>
      <div className="sh" style={{ margin: 0 }}>P0 Enhancement Alerts</div>
      <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 4, marginBottom: 12, lineHeight: 1.5 }}>
        When Omega's enhancement engine flags a <strong style={{ color: "#fca5a5" }}>P0</strong> idea
        (act-today priority), automatically send the title + rationale to your phone and/or email
        using the existing Twilio + Resend integrations. Dedupes per idea over 24h so you never get spammed.
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
        padding: 12, borderRadius: 10, marginBottom: 12,
        background: enabled ? "rgba(74,222,128,0.06)" : "rgba(255,255,255,0.02)",
        border: `1px solid ${enabled ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.08)"}`,
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>
            {enabled === null ? "Loading…" : enabled ? "Active — you'll be notified on every P0" : "Disabled"}
          </div>
          <div style={{ fontSize: 10.5, color: "var(--dim)", marginTop: 2 }}>Toggle ON to receive real SMS/email alerts.</div>
        </div>
        <button
          data-testid="p0-toggle-btn"
          onClick={() => saveToggle(!enabled)}
          disabled={enabled === null || saving}
          style={{
            position: "relative", width: 48, height: 26, borderRadius: 999,
            background: enabled ? "#4ade80" : "rgba(255,255,255,0.12)",
            border: "none", cursor: enabled === null ? "default" : "pointer",
            transition: "background 0.18s",
          }}
        >
          <span style={{
            position: "absolute", top: 3, left: enabled ? 25 : 3,
            width: 20, height: 20, borderRadius: "50%",
            background: "#fff", transition: "left 0.18s",
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          }} />
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        <div>
          <label style={fieldLabelStyle()}>Phone (Twilio SMS)</label>
          <input
            data-testid="p0-phone-input"
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+15551234567"
            style={fieldInputStyle()}
          />
        </div>
        <div>
          <label style={fieldLabelStyle()}>Email (Resend)</label>
          <input
            data-testid="p0-email-input"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            style={fieldInputStyle()}
          />
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={saveContacts} disabled={saving} data-testid="p0-save-contacts" style={secondaryBtnStyle()}>
          Save contacts
        </button>
        <button onClick={fireTest} disabled={testing} data-testid="p0-fire-test" style={primaryBtnStyle()}>
          {testing ? "Dispatching…" : "Fire test dispatch now"}
        </button>
      </div>

      {history.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
          <div style={{ fontSize: 10, color: "var(--dim)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, marginBottom: 6 }}>
            Recent dispatches
          </div>
          <div style={{ display: "grid", gap: 4 }}>
            {history.map((h, i) => (
              <div key={i} style={{ fontSize: 11, color: "var(--dim)", padding: "4px 0", display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ flex: 1, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.title || h.ideaId}</span>
                <span style={{ flexShrink: 0 }}>{(h.channels || []).join("+")}</span>
                <span style={{ flexShrink: 0, color: "var(--dim)" }}>{new Date(h.sentAt).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function fieldLabelStyle() {
  return { display: "block", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 700, marginBottom: 4 };
}
function fieldInputStyle() {
  return { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)", color: "var(--text)", fontSize: 12, fontFamily: "inherit", outline: "none" };
}
function primaryBtnStyle() {
  return { fontSize: 11, fontWeight: 700, padding: "8px 14px", borderRadius: 8, background: "rgba(167,139,250,0.14)", border: "1px solid rgba(167,139,250,0.42)", color: "#a78bfa", cursor: "pointer", fontFamily: "inherit" };
}
function secondaryBtnStyle() {
  return { fontSize: 11, fontWeight: 600, padding: "8px 14px", borderRadius: 8, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "var(--text)", cursor: "pointer", fontFamily: "inherit" };
}
